import React from 'react';
import { DashboardMetric } from '../../types';
import { motion } from 'framer-motion';
import { HiTrendingUp, HiTrendingDown, HiMinus } from 'react-icons/hi';

interface MetricCardProps {
  metric: DashboardMetric;
  index?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({ metric, index = 0 }) => {
  const formatValue = (value: number | string, format?: string): string => {
    if (typeof value === 'string') return value;

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value);
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'number':
        return new Intl.NumberFormat('en-US').format(value);
      default:
        return String(value);
    }
  };

  const getTrendIcon = () => {
    if (!metric.change) return null;

    const trend = metric.trend || (metric.change > 0 ? 'up' : metric.change < 0 ? 'down' : 'neutral');

    switch (trend) {
      case 'up':
        return <HiTrendingUp className="w-4 h-4" />;
      case 'down':
        return <HiTrendingDown className="w-4 h-4" />;
      default:
        return <HiMinus className="w-4 h-4" />;
    }
  };

  const getTrendColor = () => {
    if (!metric.change) return 'text-gray-500';

    const trend = metric.trend || (metric.change > 0 ? 'up' : metric.change < 0 ? 'down' : 'neutral');

    switch (trend) {
      case 'up':
        return 'text-success-600 dark:text-success-400 bg-success-100 dark:bg-success-900/30';
      case 'down':
        return 'text-danger-600 dark:text-danger-400 bg-danger-100 dark:bg-danger-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="card card-hover p-6 group"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            {metric.label}
          </p>
          <div className="flex items-baseline gap-2">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
              {formatValue(metric.value, metric.format)}
            </h3>
          </div>
        </div>

        {metric.icon && (
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-2xl shadow-lg transform transition-transform group-hover:scale-110 group-hover:rotate-6">
              {metric.icon}
            </div>
          </div>
        )}
      </div>

      {metric.change !== undefined && (
        <div className="mt-4 flex items-center gap-2">
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${getTrendColor()}`}>
            {getTrendIcon()}
            <span>{Math.abs(metric.change).toFixed(2)}%</span>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            vs previous period
          </span>
        </div>
      )}

      {/* Decorative gradient line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

export default MetricCard;
